import * as React from 'react'
// components
import { Tab } from '@mui/material'
import { Tabs } from 'components/UI'
import { SubHeader } from 'components/Modals/PlanItems/styles'
// constants
import { ENTITIES } from 'constants/index'
// icons
import LastPageIcon from '@mui/icons-material/LastPage'
// context
import { ReviewDrawerContext } from 'context/ReviewDrawerContext'
import { PlanDetailsContext } from 'containers/Plan/Details/layers/API'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
// graphql
import { PLAN_ACTIVITIES_LIST_QUERY } from 'graphql/plan/queries'
// hooks
import { usePlanAccess } from 'hooks/index'
// view components
import ActivitiesListContainer from 'containers/Activities/components/ActivitiesList'
import CompleteReview from 'components/Modals/PlanItems/components/CompleteReview'
import ApproveReview from 'components/Modals/PlanItems/components/ApproveReview'
import ConfirmChangePlanTypeModal from '../../../../../Modals/ChangePlanTypeModal'
import DetailsSection from 'components/PageComponents/Plans/Edit/components/SidebarInfo/components/DetailsSection'
import ObjectivesListContainer from 'containers/ObjectivesList'
import AuthorizationWarning from 'components/UI/Warnings/AuthorizationWarning'
import ReviewsList from 'components/Modals/PlanItems/components/ReviewsList'
import SidebarReviewDetailsModal from 'components/Modals/SidebarReviewResultModal'
// views
import { Wrapper, CollapseToggle } from './views'
import { DetailsTab, FlexScrollbar } from 'components/Modals/PlanItems/styles'
import { ActivitiesWrapper } from 'components/Modals/CreateReviewModal/views'
// types
import { LogEntityTypes } from 'types/graphql'

const PlanDetails: React.FC = () => {
  const [tab, setTab] = React.useState<number>(0)

  const isReview = React.useContext(ReviewDrawerContext)

  const { plan } = React.useContext(PlanDetailsContext)
  const { isSidebarCollapsed, toggleSidebarCollapsed } = React.useContext(PlanEditPageContext)

  const collapsed = !isReview && isSidebarCollapsed

  const {
    planCreateScheduledReview,
    planModifyScheduledReview,
    planDeleteScheduledReview,
    planViewCompletedReview,
    planViewInProgressReview
  } = plan.currentPermissions

  const shouldShowPlan = usePlanAccess(plan)

  // methods
  const handleSetTab = (_: any, value: number): void => setTab(value)

  return (
    <>
      <Wrapper $isReview={isReview} $collapsed={collapsed}>
        <SubHeader>
          <Tabs value={tab} onChange={handleSetTab} indicatorColor='primary' textColor='primary' variant={null}>
            <Tab label='Details' />
            {isReview && <Tab label='Plan' />}
            {isReview && <Tab label='Activity' />}
            {<Tab label='Reviews' />}
          </Tabs>
          {!isReview && (
            <CollapseToggle $collapsed={collapsed} onClick={toggleSidebarCollapsed}>
              <LastPageIcon />
            </CollapseToggle>
          )}
        </SubHeader>
        {tab === 0 && (
          <DetailsTab>
            <CompleteReview
              entity={{ ...plan, description: plan.name, entityType: ENTITIES.PLAN }}
              isFullModal={!isReview}
              isSidebar={!isReview}
            />
            {/* the review drawer has its own approve button */}
            {!isReview && (
              <ApproveReview
                entity={{ ...plan, description: plan.name, entityType: ENTITIES.PLAN }}
                isFullModal
                isSidebar
              />
            )}
            <FlexScrollbar>
              <DetailsSection entity={{ ...plan, entityType: LogEntityTypes.Plan }} />
            </FlexScrollbar>
          </DetailsTab>
        )}

        {isReview && tab === 2 && (
          <ActivitiesWrapper>
            <ActivitiesListContainer
              entity={{ ...plan, entityType: LogEntityTypes.Plan }}
              query={PLAN_ACTIVITIES_LIST_QUERY}
              variables={{
                filter: { entityId: plan.id }
              }}
            />
          </ActivitiesWrapper>
        )}

        <div style={{ height: 'calc(100% - 50px)' }}>
          {isReview &&
            tab === 1 &&
            (shouldShowPlan ? <ObjectivesListContainer plan={plan} /> : <AuthorizationWarning />)}

          {tab === (isReview ? 3 : 1) && (
            <ReviewsList
              canCreate={planCreateScheduledReview}
              canDelete={planDeleteScheduledReview}
              canModify={planModifyScheduledReview}
              canViewCompleted={planViewCompletedReview}
              canViewInProgress={planViewInProgressReview}
              entity={{ ...plan, description: plan.name, entityType: ENTITIES.PLAN }}
              isSidebar={!isReview}
            />
          )}
        </div>
      </Wrapper>

      <SidebarReviewDetailsModal />
      <ConfirmChangePlanTypeModal />
    </>
  )
}

export default PlanDetails
